import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Columns2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePremiumGate } from '../hooks/usePremiumGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Compare commentaries" — opens a sheet that loads every self-hosted
 * commentary for the current chapter (Henry, JFB, Clarke, K&D, Tyndale, Gill)
 * and lets the user tab between them. Premium/Logos-parity study feature.
 */
const CompareCommentariesButton = ({ book, chapter, language = 'en' }) => {
  const [open, setOpen] = useState(false);
  const { requirePremium, dialog: premiumDialog } = usePremiumGate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (!open || !book || !chapter) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/resources/commentaries/${encodeURIComponent(book)}/${chapter}`)
      .then((r) => {
        const srcs = r.data?.sources || [];
        if (cancelled) return;
        setSources(srcs);
        // Classic commentaries are stored in English — translate the bodies to
        // Spanish on demand (server-side cached) when the app is in Spanish.
        if (language === 'es' && srcs.length) {
          setTranslating(true);
          axios.post(`${BACKEND_URL}/api/translate`, { texts: srcs.map((s) => s.text), target: 'es' }, { timeout: 30000 })
            .then((tr) => {
              if (cancelled) return;
              const out = tr.data?.translations || [];
              setSources(srcs.map((s, i) => ({ ...s, text: out[i] || s.text })));
            })
            .catch(() => {
              if (cancelled) return;
              toast.error(language === 'es'
                ? 'No se pudo traducir ahora (mostrando en inglés). Intenta de nuevo.'
                : 'Could not translate right now (showing English).');
            })
            .finally(() => { if (!cancelled) setTranslating(false); });
        }
      })
      .catch(() => { if (!cancelled) setSources([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, book, chapter, language]);

  return (
    <>
    <Sheet open={open} onOpenChange={(v) => { if (v && !requirePremium(language === 'es' ? 'Comparar comentarios' : 'Compare Commentaries')) return; setOpen(v); }}>
      <SheetTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2.5 text-xs gap-1.5 border border-violet-300/50 bg-violet-50/50 hover:bg-violet-100/70 text-violet-900 dark:bg-violet-950/30 dark:text-violet-200 dark:border-violet-800/50 dark:hover:bg-violet-900/50"
          data-testid="compare-commentaries-btn"
          title={language === 'es' ? 'Comparar comentarios' : 'Compare commentaries'}
        >
          <Columns2 className="w-3.5 h-3.5" />
          <span className="hidden sm:inline font-medium">{language === 'es' ? 'Comparar' : 'Compare'}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-hidden flex flex-col p-0" data-testid="compare-commentaries-sheet">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-violet-200/60 dark:border-violet-800/40">
          <SheetTitle className="text-violet-900 dark:text-violet-100 flex items-center gap-2">
            <Columns2 className="w-5 h-5" />
            {language === 'es' ? 'Comparar comentarios' : 'Compare Commentaries'}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {book} {chapter}
            {' · '}
            {language === 'es' ? `${sources.length} fuentes` : `${sources.length} sources`}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
        ) : sources.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500 italic">
            {language === 'es' ? 'No hay comentarios para este capítulo.' : 'No commentaries available for this chapter.'}
          </p>
        ) : (
          <Tabs defaultValue={sources[0].id} className="flex-1 flex flex-col overflow-hidden">
            {translating && (
              <div className="mx-4 mt-2 flex items-center gap-2 rounded-md bg-violet-50 dark:bg-violet-950/40 px-3 py-1.5 text-[11px] text-violet-700 dark:text-violet-300" data-testid="compare-translating">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {language === 'es' ? 'Traduciendo al español…' : 'Translating…'}
              </div>
            )}
            <TabsList className="mx-4 mt-3 flex-wrap h-auto justify-start gap-1 bg-transparent">
              {sources.map((s) => (
                <TabsTrigger key={s.id} value={s.id} className="text-[11px] px-2.5 py-1 data-[state=active]:bg-violet-600 data-[state=active]:text-white" data-testid={`compare-tab-${s.id}`}>
                  {s.name}
                </TabsTrigger>
              ))}
            </TabsList>
            {sources.map((s) => (
              <TabsContent key={s.id} value={s.id} className="flex-1 overflow-hidden mt-2">
                <ScrollArea className="h-full px-5 pb-6">
                  <div className="text-sm leading-relaxed whitespace-pre-line text-slate-800 dark:text-slate-200" data-testid={`compare-content-${s.id}`}>
                    {s.text}
                  </div>
                  <p className="mt-4 text-[10px] text-slate-400 italic">{s.attribution}</p>
                </ScrollArea>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
    {premiumDialog}
    </>
  );
};

export default CompareCommentariesButton;
